import 'react-day-picker/lib/style.css';
import 'moment/locale/fi';
import 'moment/locale/sv';

import MomentLocaleUtils, {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  parseDate,
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  formatDate,
} from 'react-day-picker/moment';

import React from 'react';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import DayPickerInput from 'react-day-picker/lib/src/DayPickerInput';
import {
  DayPickerInputProps,
  DayPickerProps,
  InputClassNames,
  DayModifiers,
} from 'react-day-picker';
import get from 'lodash/fp/get';
import { ClassValue } from 'classnames/types';

import {
  dayPickerDefaultClasses,
  dayPickerInputDefaultClasses,
  extendClassNames,
} from './DatePicker.defaultClassNames.js';
import DatePickerInputWrapper from './DatePickerInputWrapper';
import styles from './DatePicker.module.scss';

const dayPickerClassNamesToBeAppended = {
  day: styles.datepicker__day,
  today: styles.datepicker__today,
  selected: styles.datepicker__selected,
  disabled: styles.datepicker__disabled,
  outside: styles.datepicker__outside,
  container: styles.datepicker__container,
  caption: styles.datepicker__caption,
  navButtonPrev: styles.datepicker__navButtonPrev,
  navButtonNext: styles.datepicker__navButtonNext,
  navButtonInteractionDisabled: styles.datepicker__navButtonDisabled,
  weekday: styles.datepicker__weekday,
};

const dayPickerClassNames = extendClassNames(
  dayPickerDefaultClasses,
  dayPickerClassNamesToBeAppended
);

type appendedClasses = {
  overlay: ClassValue;
  container?: ClassValue;
};

interface CustomDayPickerProps extends DayPickerProps {
  inputClassNames?: InputClassNames;
}

interface CustomDayPickerInputProps extends DayPickerInputProps {
  value?: Date;
}

export type DatePickerProps = Omit<CustomDayPickerInputProps, 'onChange'> & {
  dayPickerProps?: CustomDayPickerProps;
  label?: string;
  onChange: (selectedDate: Date, modifiers: DayModifiers) => void;
  locale?: string;
  placeholderFormat?: string;
  errorText?: string;
  helperText?: string;
  disabled?: boolean;
  showCustomLabel?: boolean;
  className?: ClassValue;
  inputRef?: React.Ref<HTMLInputElement>;
  id?: string;
  required?: boolean;
};
// Alias for backwards compatibility
export type DatePickerAttributes = DatePickerProps;

const DatePicker: React.FunctionComponent<DatePickerProps> = ({
  label,
  showCustomLabel,
  className,
  onChange,
  dayPickerProps = null,
  placeholderFormat = 'D.M.YYYY',
  locale = 'fi',
  errorText,
  helperText,
  disabled,
  inputRef,
  id,
  required,
  ...props
}) => {
  const handleDayChange = (selectedDay: Date, modifiers: DayModifiers) => {
    onChange(selectedDay, modifiers);
  };

  const dayPickerInputClassNamesToBeAppended: appendedClasses = {
    ...dayPickerProps?.inputClassNames,
    overlay: styles.datepicker__overlay,
    container: styles.datepicker,
  };

  if (className) {
    dayPickerInputClassNamesToBeAppended.container = className;
  }

  const dayPickerInputClassNames = extendClassNames(
    dayPickerInputDefaultClasses,
    dayPickerInputClassNamesToBeAppended
  );

  return (
    <DayPickerInput
      component={DatePickerInputWrapper}
      inputProps={{
        label,
        errorText,
        helperText,
        disabled,
        id,
        showCustomLabel,
        inputRef,
        required,
      }}
      onDayChange={handleDayChange}
      classNames={dayPickerInputClassNames}
      dayPickerProps={{
        ...dayPickerProps,
        classNames: dayPickerClassNames,
        renderDay: (day: Date) => <span>{day.getDate()}</span>,
        locale,
        localeUtils: MomentLocaleUtils,
      }}
      formatDate={formatDate}
      parseDate={parseDate}
      placeholder={formatDate(
        get('initialMonth', dayPickerProps) || new Date(),
        placeholderFormat,
        locale
      )}
      {...props}
    />
  );
};

export default DatePicker;
